'use client';

import React, { useEffect, useRef } from 'react';

interface Tier {
  name: string;
  price: string;          // numeric portion, e.g. "2,899"
  priceSuffix?: string;   // e.g. per month line
  currency?: string;      // "R" for ZAR
  custom?: boolean;       // Enterprise-style "Custom"
  tagline: string;        // unit/description under price
  features: string[];
  cta: { label: string; href: string };
  highlighted?: boolean;
}

const tiers: Tier[] = [
  {
    name: 'Starter',
    currency: 'R',
    price: '2,899',
    tagline: 'per month · up to 50 units',
    features: [
      'Property & tenant management',
      'Online rent collection',
      'Maintenance work orders',
      'Basic financial reports',
      'Tenant portal',
    ],
    cta: { label: 'Get started', href: '#demo' },
  },
  {
    name: 'Professional',
    currency: 'R',
    price: '7,899',
    tagline: 'per month · up to 200 units',
    highlighted: true,
    features: [
      'Everything in Starter',
      'Lease management + ASC 842',
      'Commercial property support',
      'AI lease abstraction (50/mo)',
      'Advanced analytics',
      'API access & integrations',
    ],
    cta: { label: 'Start free trial', href: '#demo' },
  },
  {
    name: 'Enterprise',
    custom: true,
    price: 'Custom',
    tagline: 'unlimited units · dedicated support',
    features: [
      'Everything in Professional',
      'Affordable & public housing',
      'Facilities IWMS module',
      'Custom integrations & SSO',
      'Dedicated success manager',
      'SLA & uptime guarantee',
    ],
    cta: { label: 'Contact sales →', href: '#demo' },
  },
];

function Check() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-[#22C55E]"
      aria-hidden
    >
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

function PriceBlock({ tier }: { tier: Tier }) {
  if (tier.custom) {
    return (
      <div className="flex items-baseline gap-1">
        <span
          className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0]"
          style={{ fontSize: 'clamp(2.25rem, 4vw, 3.5rem)', lineHeight: 1 }}
        >
          Custom
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-1">
      <span className="text-lg font-manrope font-semibold text-[#EEEEF0] mt-2">
        {tier.currency}
      </span>
      <span
        className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0]"
        style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', lineHeight: 1 }}
      >
        {tier.price}
      </span>
    </div>
  );
}

function TierCard({ tier }: { tier: Tier }) {
  const highlight = tier.highlighted;

  return (
    <div
      className={`relative flex ${
        highlight ? 'md:-my-6 lg:-my-8 z-10' : ''
      }`}
    >
      {highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#7C3AED] text-[10px] font-mono font-semibold text-white tracking-widest uppercase shadow-[0_0_20px_rgba(124,58,237,0.5)] whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-white/90" />
            Most popular
          </span>
        </div>
      )}

      <div
        className={`bento-card card-shine relative flex flex-col w-full p-5 sm:p-7 lg:p-8 ${
          highlight ? 'pricing-highlight md:py-10 lg:py-12' : ''
        }`}
      >
      {/* Tier name */}
      <div className="text-[10px] font-mono text-[#9999A8] tracking-widest uppercase mb-6">
        {tier.name}
      </div>

      {/* Price */}
      <PriceBlock tier={tier} />

      {/* Tagline */}
      <p className="text-xs font-mono text-[#9999A8] mt-4">{tier.tagline}</p>

      {/* Divider */}
      <div className="h-px bg-[rgba(255,255,255,0.06)] my-6" />

      {/* Features */}
      <ul className="flex flex-col gap-3 mb-8">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-[#9999A8]">
            <span className="mt-0.5">
              <Check />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className="mt-auto">
        {highlight ? (
          <a
            href={tier.cta.href}
            className="cta-primary flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl text-sm tracking-tight"
          >
            {tier.cta.label}
          </a>
        ) : (
          <a
            href={tier.cta.href}
            className="cta-secondary flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl text-sm"
          >
            {tier.cta.label}
          </a>
        )}
      </div>
      </div>
    </div>
  );
}

export default function PricingSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="pricing" className="py-16 sm:py-24 px-4 sm:px-6 relative">
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 50% 30%, rgba(124,58,237,0.08) 0%, transparent 70%)',
        }}
      />

      <div ref={ref} className="reveal-blur max-w-6xl mx-auto relative z-10">
        {/* Heading */}
        <div className="flex flex-col items-center text-center gap-4 mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(124,58,237,0.25)]">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-light status-pulse" />
            <span className="text-[10px] font-mono text-violet-light tracking-widest uppercase">
              Pricing
            </span>
          </div>
          <h2
            className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0] leading-tight"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)' }}
          >
            Simple, transparent{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              plans
            </span>
          </h2>
          <p className="text-[#9999A8] text-base max-w-xl">
            Start free, scale as you grow. No hidden fees. Prices in ZAR, billed monthly.
          </p>
        </div>

        {/* Tier grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {tiers.map((t) => (
            <TierCard key={t.name} tier={t} />
          ))}
        </div>

        {/* Footnote */}
        <p className="text-center text-xs font-mono text-[#5A5A6E] mt-10">
          Annual billing saves 20% · VAT inclusive · Cancel anytime
        </p>
      </div>
    </section>
  );
}
